import { AgeGateBlacklist } from '../../domain/conf/age-gate-blacklist'
import { Utils } from '../../utils/common'
import { DOMEvents, DOMUtils } from '../../utils/dom'
import { SimpleEvents } from '../../utils/events'
import { AdService } from '../ads'
import { IAd } from '../ads/ad'
import { AdEvents, IAdEvent } from '../ads/ad/events'
import { AgeGateEvents } from './event'

// require styles (webpack)
import './styles.css' // tslint:disable-line: no-import-side-effect

interface IAgeGate {
    ad: IAd
    veil: Element
}

export class AgeGateService extends SimpleEvents {
    public static DOM = {
        className: {
            gate: 'agegate',
            msg: 'agegate-msg',
            veil: 'agegate-veil',
        },
        param: {
            test: 'agegate', // for testing (shows on all ads)
            verified: 'utm_agegate' // sets user as verified
        }
    }
    public static Message = {
        confirm: 'Verify',
        gated: 'This content is intended for audiences over the age of 19+. ' +
            'Please verify you are legal age of majority in your province.',
        prompt: 'I am 19+ years of age',
        verified: 'You are seeing this ad because you are 19+ of age.'
    }

    public ageVerified: boolean
    public gates: { [_: string]: IAgeGate } = {}
    protected verifying = false

    /**
     * Checks if advertiser requires age verification.
     */
    public static requiresVerification(advertiserId: number): boolean {
        return AgeGateBlacklist.advertiser.ids.includes(advertiserId)
    }

    /**
     * Stores verified state and registers listener(s).
     */
    public initialize(ageVerified: boolean) {
        this.ageVerified = ageVerified
        document.addEventListener(AdEvents.DISPLAY, this.onAdLoaded.bind(this))

        // age verified via query param
        if (!this.ageVerified && Utils.params(AgeGateService.DOM.param.verified)) {
            this.onAgeVerified()
        }
    }

    /**
     * Applies 'age verified' messaging.
     */
    protected applyMessaging(ads: IAd[] = Object.values(this.gates).map((_) => _.ad)) {
        ads
            .filter((ad) => ad && !(ad.isOutOfPage() || ad.isWallpaper()))
            .forEach((ad) => {
                const el = document.createElement('div')
                ad.el.classList.remove(AgeGateService.DOM.className.gate)
                ad.el.firstElementChild.appendChild(el)
                el.classList.add(AgeGateService.DOM.className.msg)
                el.innerText = AgeGateService.Message.verified
                el.setAttribute('style', `width:${ad.sizeRendered[0]}px`)
            })
    }

    /**
     * init age gating form or msg
     */
    protected async onAdLoaded(e: IAdEvent) {
        const ad = e.detail
        const isGated =
            Utils.params(AgeGateService.DOM.param.test) || AgeGateService.requiresVerification(ad.advertiser)

        if (ad.isOutOfPage() || !isGated) {
            return
        }

        // wallpapers get veil but not form
        if (ad.isWallpaper()) {
            if (!this.ageVerified) {
                await this.wallpaperReady()
                DOMUtils.nodes(document.body.querySelectorAll(AdService.Wallpaper.selector))
                    .forEach((el: Element) => {
                        this.gates[el.id] = { ad, veil: this.veil('100%', '100%', false) }
                        el.prepend(this.gates[el.id].veil)
                    })
            }
            return
        }

        // already verified, show messaging
        if (this.ageVerified) {
            return this.applyMessaging([ad])
        }

        // show verification form
        const veil = this.veil(`${ad.sizeRendered[0]}px`, `${ad.sizeRendered[1]}px`)
        ad.el.classList.add(AgeGateService.DOM.className.gate)
        ad.el.firstElementChild.prepend(veil)
        this.gates[ad.id] = { ad, veil }
    }

    /**
     * Removes gates, applies messaging and fires 'age verified' event.
     */
    protected onAgeVerified() {
        this.ageVerified = true
        Object.values(this.gates).forEach(({ veil }) => veil.remove())
        this.applyMessaging()
        this.dispatchEvent(Utils.event(AgeGateEvents.VERIFIED))
    }

    /**
     * Synchronizes state of gates upon user input.
     */
    protected onChange(e: Event) {
        this.verifying = (e.target as HTMLInputElement).checked
        Object.values(this.gates).forEach(({ ad, veil }) => {
            if (ad.isWallpaper() || ad.isOutOfPage()) {
                return
            }

            veil.querySelector('input').checked = this.verifying
            veil.querySelector('button').disabled = !this.verifying
        })
    }

    /**
     * Creates veil for given ad size.
     */
    protected veil(width: string, height: string, hasForm = true) {
        const veil = document.createElement('div')
        veil.classList.add(AgeGateService.DOM.className.veil)
        veil.setAttribute(DOMUtils.attrs.style, `height:${height};width:${width}`)

        if (hasForm) {
            const btn = document.createElement('button')
            const checkbox = document.createElement('input')
            const form = document.createElement('form')
            const inputs = document.createElement('p')
            const label = document.createElement('label')
            const msg = document.createElement('p')

            btn.innerText = AgeGateService.Message.confirm
            btn.disabled = !this.verifying
            checkbox.addEventListener(DOMEvents.CHANGE, this.onChange.bind(this))
            checkbox.checked = this.verifying
            checkbox.type = 'checkbox'
            form.addEventListener(DOMEvents.SUBMIT, this.onAgeVerified.bind(this))
            form.appendChild(msg)
            form.appendChild(inputs)
            inputs.appendChild(label)
            inputs.appendChild(btn)
            label.innerText = AgeGateService.Message.prompt
            label.prepend(checkbox)
            msg.innerText = AgeGateService.Message.gated
            veil.appendChild(form)
        }

        return veil
    }

    /**
     * Watches for proprietary wallpaper element to be added.
     */
    protected async wallpaperReady(): Promise<void> {
        return new Promise((resolve) => {
            if (document.body.querySelector(AdService.Wallpaper.selector)) {
                return resolve()
            }
            new MutationObserver((mrs: MutationRecord[], mo: MutationObserver) => {
                mrs.forEach((mr) => {
                    if (mr.type === 'childList') {
                        DOMUtils.nodes(mr.addedNodes).forEach((n: HTMLElement) => {
                            if (n.id === AdService.Wallpaper.readyId) {
                                mo.disconnect()
                                resolve()
                            }
                        })
                    }
                })
            })
                .observe(document.body, { childList: true, subtree: true })
        })
    }
}
